import React, { useEffect } from 'react';
import styled from 'styled-components';
import { Button } from '@zocdoc-frontend-common/components-button';
import PaddedContainer from '@zocdoc-frontend-common/components-padded-container';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import {
    withCustomMetrics,
    EVENT_INITIATORS,
    INTERACTION_TYPES,
} from '@zocdoc-frontend-common/utils-frontend-metrics';
import { skipTranslation } from '@zocdoc-frontend-common/utils-translation';
import { InlineLink } from '@zocdoc-mezzanine/components-link';
import VirtualVisitSearchResults from '../VirtualVisitSearchResults';

function CoronaErrorView(props) {
    const {
        officePhoneNumber,
        directoryId,
        hasVirtualLocations,
        profileUrl,
        sendMetric,
        professionalName,
        firstVirtualLocation,
    } = props;

    useEffect(() => {
        sendMetric({
            initiator: EVENT_INITIATORS.USER,
            interactionType: INTERACTION_TYPES.VIEW,
            eventDetails: {
                hasVirtualLocations,
                hasVirtualLocationInSameState:
                    firstVirtualLocation !== undefined,
            },
        });
    }, [sendMetric, hasVirtualLocations, firstVirtualLocation]);

    const showVirtualSelfLink = hasVirtualLocations && firstVirtualLocation;

    const headerText = showVirtualSelfLink
        ? `Your in-person booking couldn't be completed`
        : `Your booking couldn't be completed`;
    const vvView =
        !showVirtualSelfLink && directoryId < 0 ? (
            <VirtualVisitSearchResults data-test="virtual-visit-search" />
        ) : null;

    return (
        <>
            <Wrapper data-test="corona-error-view">
                <Header>{headerText}</Header>
                {showVirtualSelfLink ? (
                    <SelfLink
                        professionalName={professionalName}
                        profileUrl={profileUrl}
                        firstVirtualLocation={firstVirtualLocation}
                        officePhoneNumber={officePhoneNumber}
                        showVirtualSelfLink={showVirtualSelfLink}
                    />
                ) : (
                    <InPersonMessaging
                        officePhoneNumber={officePhoneNumber}
                        professionalName={professionalName}
                    />
                )}
            </Wrapper>
            {vvView}
        </>
    );
}

const SelfLink = ({
    professionalName,
    profileUrl,
    firstVirtualLocation,
    officePhoneNumber,
}) => (
    <>
        <Paragraph data-test={'corona-self-link'}>
            Based on your responses to the previous questions, and out of an
            abundance of caution with respect to the coronavirus (COVID-19),
            we're unable to complete in-person booking at this time.{' '}
            <span className={skipTranslation}>{professionalName}</span> offers
            video visit appointments so you can get the care you need without
            leaving your home.
        </Paragraph>
        <ViewVideoVisitAvailability
            href={`${profileUrl}?LocIdent=${firstVirtualLocation.monolithId}`}
        >
            View video visit availability
        </ViewVideoVisitAvailability>
        <Paragraph>
            Schedule a video visit or call the practice at{' '}
            <InlineLink href={`tel:${officePhoneNumber}`}>
                {officePhoneNumber}
            </InlineLink>{' '}
            to discuss your needs and an appropriate care plan.
        </Paragraph>
    </>
);

const InPersonMessaging = ({ officePhoneNumber, professionalName }) => (
    <Paragraph>
        For everyone’s safety, we’re recommending that patients who have or may
        have COVID-19 call the doctor before scheduling an in-person visit. You
        can reach <span className={skipTranslation}>{professionalName}</span> at{' '}
        <InlineLink
            data-test={'in-person-office-phone-number'}
            href={`tel:${officePhoneNumber}`}
        >
            {officePhoneNumber}
        </InlineLink>{' '}
        to discuss your needs and an appropriate care plan.
    </Paragraph>
);

export default withCustomMetrics({
    component: 'CoronaErrorView',
    element: 'CoronaErrorView',
})(CoronaErrorView);

const Wrapper = styled(PaddedContainer)`
    text-align: center;
    max-width: 800px;
    margin: 72px auto;
`;

const Header = styled.h1`
    font: 24px/32px ${FontFamily.SharpSansBold};
    margin: 16px auto;
    max-width: 540px;
`;

const Paragraph = styled.p`
    font: 16px/1.5 ${FontFamily.SharpSansMedium};
    margin: 18px auto;
`;

const ViewVideoVisitAvailability = styled(Button)`
    font-size: 15px;
    width: 100%;
    margin: 14px auto;
`;
